import type { CodeableConcept, Practitioner } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import type { Participant } from '@seen/core/models/patients/participant'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType, parseReferenceString } from '@seen/core/utils'
import { Button, KBD, TypographyDetail } from '@seen/ui'
import { IconLink } from '@tabler/icons-react'
import type { DefaultError } from '@tanstack/react-query'
import type { Editor } from '@tiptap/core'
import { compact, uniq } from 'lodash-es'
import {
  type JSX,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { TaskInboxNotificationCheckbox } from '#src/components/app/TaskInboxNotificationCheckbox.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { HoverTask } from '#src/components/HoverTask.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { MentionResource } from '#src/components/MentionResource.tsx'
import { OwnerComboboxButton } from '#src/components/OwnerComboboxButton.tsx'
import { ParticipantComboboxButton } from '#src/components/ParticipantComboboxButton.tsx'
import { extractMentionReferences } from '#src/components/rich-text-editor/mention/extractMentionReferences.ts'
import { RequiredAnswersDialog } from '#src/components/rich-text-editor/required/RequiredAnswersDialog.tsx'
import { editorRequiredGaps } from '#src/components/rich-text-editor/required/requiredGaps.ts'
import {
  holdsAnswers,
  snapshotNoteCaptures,
  useNoteCaptureWriter,
} from '#src/components/rich-text-editor/required/useNoteCaptureWriter.ts'
import { useRequiredAnswers } from '#src/components/rich-text-editor/required/useRequiredAnswers.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { TaskPriorityCombobox } from '#src/components/task/TaskPriorityCombobox.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { TaskStateCombobox } from '#src/components/task/TaskStateCombobox.tsx'
import { TaskStateText } from '#src/components/task/TaskStateText.tsx'
import { TaskLabelComboboxButton } from '#src/components/TaskLabelComboboxButton.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { ConfigContext } from '#src/contexts/config/ConfigContext.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { useRelatedParticipantReference } from '#src/hooks/useRelatedParticipantReference.ts'
import { useResetOnSubmit } from '#src/hooks/useResetOnSubmit.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'
import { getTimelineItemIdFromUrl } from '#src/hooks/useTimelineItemUrl.ts'

export function TaskComposer(): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const currentPractitioner = useCurrentPractitioner()

  const open = useTaskComposerStore((state) => state.open)
  const setOpen = useTaskComposerStore((state) => state.setOpen)
  const setDefaultValues = useTaskComposerStore(
    (state) => state.setDefaultValues
  )
  const onTaskCreated = useTaskComposerStore((state) => state.onTaskCreated)
  const setOnTaskCreated = useTaskComposerStore(
    (state) => state.setOnTaskCreated
  )
  const onClose = useTaskComposerStore((state) => state.onClose)
  const setOnClose = useTaskComposerStore((state) => state.setOnClose)

  useHotkeys(Hotkeys.TaskComposer, () => setOpen(true), {
    preventDefault: true,
    enabled: !isReadOnly,
  })

  const formDefaultValues = useFormDefaultValues()

  const form = useForm({
    schema: formSchema,
    defaultValues: formDefaultValues,
  })
  const inboxNotificationOverrideEnabled = useFeatureFlag(
    featureFlags.taskInboxNotificationOverride
  )
  const inboxNotificationOverridden = useRef(false)
  const defaultValues = useTaskComposerStore((state) => state.defaultValues)
  const previousDefaultValues = useRef(defaultValues)

  const state = form.watch('state')
  const title = form.watch('title')
  const owner = form.watch('owner')
  const participant = form.watch('participant')
  const focus = form.watch('focus')
  const basedOn = form.watch('basedOn')

  const linkedReference = useMemo(() => {
    if (focus) {
      return parseReferenceString(focus)
    }
    const firstBasedOn = basedOn?.[0]
    return firstBasedOn ? parseReferenceString(firstBasedOn) : null
  }, [focus, basedOn])
  const linkedParticipant = useRelatedParticipantReference(linkedReference)
  const linkedParticipantRef = linkedParticipant?.reference ?? null
  const isParticipantLockedToLinked = Boolean(linkedParticipantRef)

  useEffect(() => {
    if (previousDefaultValues.current !== defaultValues) {
      inboxNotificationOverridden.current = false
      previousDefaultValues.current = defaultValues
    }
    form.reset({
      ...formDefaultValues,
      sendInboxNotification: inboxNotificationOverridden.current
        ? form.getValues('sendInboxNotification')
        : formDefaultValues.sendInboxNotification,
    })
  }, [defaultValues, formDefaultValues, form])

  useEffect(() => {
    if (
      !inboxNotificationOverrideEnabled ||
      inboxNotificationOverridden.current
    ) {
      return
    }
    form.setValue(
      'sendInboxNotification',
      Boolean(owner) && owner !== currentPractitioner.reference.reference
    )
  }, [
    currentPractitioner.reference.reference,
    form,
    inboxNotificationOverrideEnabled,
    owner,
  ])

  // Force participant to match linked resource's participant
  useEffect(() => {
    if (!linkedParticipantRef) {
      return
    }
    if (participant !== linkedParticipantRef) {
      form.setValue('participant', linkedParticipantRef)
    }
  }, [linkedParticipantRef, participant, form])

  // Set participant from patient mention in title (only if not already set)
  useEffect(() => {
    if (participant || isParticipantLockedToLinked) {
      return
    }

    const mentions = extractMentionReferences(title)
    const patientMention = mentions.find((ref) => isOfType(ref, 'Patient'))
    if (patientMention?.reference) {
      form.setValue('participant', patientMention.reference)
    }
  }, [title, participant, isParticipantLockedToLinked, form])

  const participants = useParticipants()
  const linkOrigin = useContext(ConfigContext)?.portalOrigin
  // The task's participant is the record its title and description can
  // draw variables from, so picking one changes what the @ picker offers.
  // `null` when the task has none: the editors then record for no one rather
  // than the participant whose page the composer was opened on.
  const selectedParticipant = useMemo(
    () =>
      participant
        ? participants.find((p) => p.reference.reference === participant)
        : null,
    [participants, participant]
  )

  const close = useCallback(() => {
    inboxNotificationOverridden.current = false
    setOpen(false)
    setDefaultValues({})
    setOnTaskCreated(undefined)
    onClose?.()
    setOnClose(undefined)
  }, [setDefaultValues, setOpen, setOnTaskCreated, onClose, setOnClose])

  const createMutation = useCreateMutation<StandardTask>()

  // Answers given in the title or description are held until the task is
  // created, then recorded for its participant.
  const titleEditorRef = useRef<Editor | null>(null)
  const descriptionEditorRef = useRef<Editor | null>(null)
  const getDescriptionEditor = useCallback(
    () => descriptionEditorRef.current,
    []
  )
  const required = useRequiredAnswers({
    getEditor: getDescriptionEditor,
    surface: 'task',
    action: 'Submit',
  })
  const writeCaptures = useNoteCaptureWriter()

  const submit = form.handleSubmit(async (values) => {
    const editors = compact([
      titleEditorRef.current,
      descriptionEditorRef.current,
    ]).filter((editor) => !editor.isDestroyed)
    const gaps = editors.flatMap(editorRequiredGaps)
    if (gaps.length && (await required.check(gaps)) === 'answer') {
      return
    }
    const captures = editors.map(snapshotNoteCaptures).filter(holdsAnswers)
    const task = new StandardTask({
      requester: currentPractitioner.reference,
    })
    task.code = values.code ?? undefined
    task.title = values.title
    task.description = values.description
    task.state = values.state
    task.priority = values.priority ?? undefined
    task.ownerPractitioner = values.owner
      ? parseReferenceString<Practitioner>(values.owner)
      : undefined
    task.requester = currentPractitioner.reference
    task.sendInboxNotification =
      inboxNotificationOverrideEnabled && inboxNotificationOverridden.current
        ? values.sendInboxNotification
        : undefined
    task.dueOn = values.dueOn ?? undefined
    task.focus = values.focus ? parseReferenceString(values.focus) : undefined
    task.labels = compact(values.labels.map((l) => parseReferenceString(l)))

    const extractedTimelineRefStrings = uniq([
      ...extractAllTimelineItemIds(values.description),
      ...extractAllTimelineItemIds(values.title),
    ]).map((itemId) => `Communication/${itemId}`)

    const allBasedOnRefs = compact(
      uniq([...values.basedOn, ...extractedTimelineRefStrings]).map((s) =>
        parseReferenceString(s)
      )
    )
    if (allBasedOnRefs.length) {
      task.basedOn = allBasedOnRefs
    }

    let participant: Participant | undefined
    if (values.participant) {
      participant = participants.find(
        (p) => p.reference.reference === values.participant
      )
      task.participant = participant?.reference
    }

    const subject = participant?.reference
    if (captures.length && !subject) {
      toast.info('Answers in the task were not recorded: it has no participant')
    }
    createMutation.mutate(task, {
      onSuccess: (t) => {
        onTaskCreated?.(t)
        if (subject && captures.length) {
          writeCaptures(captures, {
            noteKey: t.reference.reference ?? '',
            subject,
          }).catch((error: DefaultError) => {
            toast.error(
              `The task was created, but its answers were not recorded${
                error instanceof Error ? `: ${error.message}` : ''
              }`
            )
          })
        }
        toast.success(
          <div className="flex flex-1 flex-col gap-2">
            <TypographyDetail>Task created</TypographyDetail>
            <HoverTask task={t} />
            <div className="flex items-center justify-between gap-2">
              <TextLink
                to={
                  participant
                    ? `${participant.relativeUrl}/tasks/${t.id}`
                    : `/tasks/${t.id}`
                }
              >
                View task
              </TextLink>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      void navigator.clipboard.writeText(
                        `${linkOrigin ?? window.location.origin}/tasks/${t.id}`
                      )
                      toast.info('Task URL copied to clipboard')
                    }}
                  >
                    <IconLink className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="center" className="z-toast">
                  Copy task URL
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )
      },
    })
    close()
  })

  useResetOnSubmit(form, formDefaultValues)

  if (isReadOnly) {
    return null
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setOpen(nextOpen)
        } else {
          close()
        }
      }}
    >
      <DialogDisplay>
        <Form onSubmit={submit}>
          <DialogHeader className="py-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field: { value, ...fieldRest } }) => (
                <RichTextEditor
                  editorRef={titleEditorRef}
                  observationRecordStatus="deferred"
                  defaultValue={value}
                  placeholder="Task Title"
                  variant="unstyled"
                  className="flex-1"
                  contentClassName="text-xl font-bold"
                  autofocus="end"
                  participant={selectedParticipant}
                  {...fieldRest}
                />
              )}
            />
          </DialogHeader>
          <DialogContent className="pt-0">
            <FormField
              control={form.control}
              name="description"
              render={({ field: { value, ...fieldRest } }) => (
                <RichTextEditor
                  editorRef={descriptionEditorRef}
                  observationRecordStatus="deferred"
                  defaultValue={value}
                  placeholder="Add description…"
                  variant="unstyled"
                  className="min-h-16"
                  contentClassName="leading-7"
                  participant={selectedParticipant}
                  {...fieldRest}
                />
              )}
            />
          </DialogContent>
          <DialogFooter className="py-3">
            <div className="flex size-full flex-col gap-3">
              <div className="flex flex-wrap justify-start gap-2">
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <TaskStateCombobox onSelect={onChange} selected={value}>
                      <Button variant="outline" className="gap-2" {...rest}>
                        <IconTaskState state={value} />
                        <TaskStateText state={value} />
                      </Button>
                    </TaskStateCombobox>
                  )}
                />
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <TaskPriorityCombobox onSelect={onChange} selected={value}>
                      <Button variant="outline" className="gap-2" {...rest}>
                        <TaskPriorityIcon
                          priority={value ?? undefined}
                          isTaskCompletedOrCancelled={StandardTask.isCompletedOrCancelled(
                            state
                          )}
                        />
                        {value ? StandardTask.priorityText(value) : 'Priority'}
                      </Button>
                    </TaskPriorityCombobox>
                  )}
                />
                <FormField
                  control={form.control}
                  name="owner"
                  render={({ field }) => <OwnerComboboxButton {...field} />}
                />
                <FormField
                  control={form.control}
                  name="dueOn"
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      placeholder="Due"
                      clearText="Remove due date"
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="participant"
                  render={({ field }) =>
                    isParticipantLockedToLinked ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- disabled button swallows pointer/focus events so wrapper span must be focusable for Radix tooltip to open */}
                          <span tabIndex={0}>
                            <ParticipantComboboxButton
                              {...field}
                              disabled
                              className="pointer-events-none"
                            />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          Participant is locked to the linked resource's subject
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <ParticipantComboboxButton {...field} />
                    )
                  }
                />
                <FormField
                  control={form.control}
                  name="labels"
                  render={({ field }) => <TaskLabelComboboxButton {...field} />}
                />
              </div>
              <div className="flex min-w-0 flex-1">
                {linkedReference && (
                  <MentionResource reference={linkedReference} />
                )}
              </div>
              <div className="-mx-6 flex items-center justify-between border-t px-6 pt-3">
                <FormField
                  control={form.control}
                  name="sendInboxNotification"
                  render={({ field: { value, onChange } }) => (
                    <TaskInboxNotificationCheckbox
                      value={value}
                      owner={owner}
                      onChange={(nextValue) => {
                        inboxNotificationOverridden.current = true
                        onChange(nextValue)
                      }}
                    />
                  )}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="submit" className="ml-auto">
                      Create Task
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="flex items-center gap-1 px-2"
                  >
                    <div className="flex gap-1">
                      <KBD>⌘</KBD>
                      <KBD>Enter</KBD>
                    </div>
                    <TypographyDetail className="text-muted-foreground">
                      to save task
                    </TypographyDetail>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </DialogFooter>
        </Form>
        <RequiredAnswersDialog {...required.dialog} />
      </DialogDisplay>
    </Dialog>
  )
}

const formSchema = z.object({
  title: z.string().min(1),
  description: z.string(),
  state: StandardTask.stateSchema,
  priority: StandardTask.prioritySchema.nullable(),
  owner: z.string().nullable(),
  dueOn: z.string().date().nullable(),
  participant: z.string().nullable(),
  focus: z.string().nullable(),
  basedOn: z.array(z.string()),
  /* z.custom<CodeableConcept>() causes TS2589 (excessively deep) because
     FHIR's recursive Extension type blows up React Hook Form inference. */
  code: z.any().nullable(),
  labels: z.array(z.string()),
  sendInboxNotification: z.boolean(),
})

type FormDefaultValues = Omit<z.infer<typeof formSchema>, 'code'> & {
  code: CodeableConcept | null
}

function useFormDefaultValues(): FormDefaultValues {
  const params = useParams()
  const paramsPatientId = params.patientId

  const defaultValues = useTaskComposerStore((state) => state.defaultValues)
  const storeParticipant = useTaskComposerStore(
    (state) => state.currentParticipant
  )
  const isInDesk = useTaskComposerStore((state) => state.isInDesk)
  const currentParticipant = isInDesk ? undefined : storeParticipant
  const currentPractitioner = useCurrentPractitioner()
  const owner =
    defaultValues.ownerPractitioner?.reference ??
    currentPractitioner.reference.reference
  const participant =
    defaultValues.participant?.reference ??
    currentParticipant?.reference ??
    (paramsPatientId ? `Patient/${paramsPatientId}` : null)

  return useMemo(
    () => ({
      title: defaultValues.title ?? '',
      description: defaultValues.description ?? '',
      state: defaultValues.state ?? 'todo',
      priority: defaultValues.priority ?? null,
      owner,
      dueOn: defaultValues.dueOn ?? null,
      participant,
      focus: defaultValues.focus?.reference ?? null,
      basedOn: compact(
        defaultValues.basedOn?.map((ref) => ref.reference) ?? []
      ),
      labels: compact(
        defaultValues.labels?.map((label) => label.reference) ?? []
      ),
      sendInboxNotification: owner !== currentPractitioner.reference.reference,
      code: defaultValues.code ?? null,
    }),
    [currentPractitioner.reference.reference, defaultValues, owner, participant]
  )
}

/*
 * Extracts every timeline-item id referenced by an <a href> in the HTML
 * description. Matches both `#item-<id>` (canonical) and `#comment-<id>`
 * (legacy) hash formats — so the returned ids can refer to any
 * Communication that appears in a participant timeline (comments and faxes
 * today). Caller treats results as Communication ids; basedOn on a task
 * legitimately accepts arbitrary Communications under FHIR, so faxes
 * pasted as deep-links will appear there too.
 */
function extractAllTimelineItemIds(htmlString: string): string[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')
  const links = doc.querySelectorAll('a[href]')
  const itemIds: string[] = []
  links.forEach((link) => {
    const href = link.getAttribute('href') ?? ''
    const itemId = getTimelineItemIdFromUrl(href)
    if (itemId) {
      itemIds.push(itemId)
    }
  })

  return itemIds
}
